//GraderAdminDashboard.js

import React, { useState, useEffect, useMemo } from "react";
import "./GraderDashboard.css";
import LoadingComponent from "./LoadingComponent";
import RegradeModal from "./RegradeModal";

const GraderAdminDashboard = ({ trainerID }) => {
	const API = process.env.REACT_APP_API_URL;

	const [regradeRequests, setRegradeRequests] = useState([]);
	const [pendingAppealsCount, setPendingAppealsCount] = useState(0);
	const [isLoading, setIsLoading] = useState(true);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedRequest, setSelectedRequest] = useState(null);

	const authHeaders = useMemo(
		() => ({
			"x-trainer-id": trainerID,
		}),
		[trainerID],
	);

	const fetchJson = async (url, options = {}) => {
		const response = await fetch(url, options);
		const contentType = response.headers.get("content-type") || "";
		const isJson = contentType.includes("application/json");
		const body = isJson ? await response.json() : await response.text();

		if (!response.ok) {
			const message =
				typeof body === "string"
					? body
					: body?.error || body?.message || JSON.stringify(body);
			throw new Error(message);
		}

		return body;
	};

	const refreshData = async () => {
		try {
			const [regradeData, appealsCount] = await Promise.all([
				fetchJson(`${API}/failed-wo/regrade-requests`, {
					headers: authHeaders,
				}),
				fetchJson(`${API}/failed-wo/pending-appeals-count`, {
					headers: authHeaders,
				}),
			]);

			setRegradeRequests(Array.isArray(regradeData) ? regradeData : []);
			setPendingAppealsCount(Number(appealsCount || 0));
		} catch (error) {
			console.error("Error refreshing data:", error);
		}
	};

	useEffect(() => {
		let mounted = true;

		const fetchData = async () => {
			try {
				if (!trainerID) return;

				const [regradeData, appealsCount] = await Promise.all([
					fetchJson(`${API}/failed-wo/regrade-requests`, {
						headers: authHeaders,
					}),
					fetchJson(`${API}/failed-wo/pending-appeals-count`, {
						headers: authHeaders,
					}),
				]);

				if (!mounted) return;

				setRegradeRequests(Array.isArray(regradeData) ? regradeData : []);
				setPendingAppealsCount(Number(appealsCount || 0));
			} catch (error) {
				console.error("Error fetching data:", error);
			} finally {
				if (mounted) setIsLoading(false);
			}
		};

		fetchData();

		return () => {
			mounted = false;
		};
	}, [API, authHeaders, trainerID]);

	const handleOpenModal = (request) => {
		setSelectedRequest(request);
		setIsModalOpen(true);
	};

	if (isLoading) {
		return <LoadingComponent />;
	}

	return (
		<div className="grader-admin-dashboard">
			<h1>Grader Dashboard</h1>
			<p>Welcome to the Grader Dashboard, {trainerID}</p>

			<div className="dashboard-cards mb-3">
				<div className="dashboard-card">
					<div className="card-title">Pending Appeals</div>
					<div className="card-value">{pendingAppealsCount}</div>
				</div>
			</div>

			<table>
				<thead>
					<tr>
						<th>Alias</th>
						<th>WO Number</th>
						<th>Date of Appeal</th>
						<th>Template</th>
						<th>Total Score</th>
						<th>Action</th>
					</tr>
				</thead>
				<tbody>
					{regradeRequests.map((request, index) => (
						<tr key={`${request.wo_num}-${index}`}>
							<td>{request.pp_alias}</td>
							<td>{request.wo_num}</td>
							<td>
								{request.regrade_gen_date
									? String(request.regrade_gen_date).split("T")[0]
									: "N/A"}
							</td>
							<td>{request.template}</td>
							<td>{request.total_score}</td>
							<td>
								<button onClick={() => handleOpenModal(request)}>
									View Details
								</button>
							</td>
						</tr>
					))}
				</tbody>
			</table>

			{isModalOpen && (
				<RegradeModal
					request={selectedRequest}
					onClose={() => setIsModalOpen(false)}
					refreshData={refreshData}
					trainerID={trainerID}
				/>
			)}
		</div>
	);
};

export default GraderAdminDashboard;
